import countries from 'i18n-iso-countries';
import enLocale from 'i18n-iso-countries/langs/en.json';
import dateFormat from 'dateformat';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import configData from '../config.json';
import { appNotificationsTypes } from './constants';

import { setNotification } from '../actions/appNotifications';
import { generalEnums, rateEnum } from './enums';

countries.registerLocale(enLocale);

export const sortArrayOfObjectsByDate = (array, prop, additionalProp1, additionalProp2) => {
  const sortedArray = array.sort((current, next) => {
    if (new Date(current[prop]) === new Date(next[prop])) return 0;
    return new Date(current[prop]) > new Date(next[prop]) ? -1 : 1;
  });

  if (additionalProp1 && additionalProp2) {
    return [
      ...sortArrayOfObjectsByDate(
        array.filter((item) => item[additionalProp1]),
        additionalProp2
      ),
      ...sortedArray.filter((item) => !item[additionalProp1])
    ];
  }
  return sortedArray;
};

export const validatePBData = (data) => {
  const errors = {
    JobExperienceDetails: [],
    EducationDetails: [],
    TrainingDetails: [],
    RequireAtLeastOneItem: []
  };
  data.JobExperienceDetails?.forEach((job) => {
    if (!job.Title || !job.EmployerName || !job.Description || !job.StartDate) errors.JobExperienceDetails.push(job);
  });

  data.EducationDetails?.forEach((edu) => {
    if (!edu.SchoolName || !edu.Degree?.Name || !edu.Major || !edu.StartDate || !edu.EndDate)
      errors.EducationDetails.push(edu);
  });

  data.TrainingDetails?.forEach((training) => {
    if (!training.CertificationType) errors.TrainingDetails.push(training);
  });

  /* Tenporarily comented because of requirements from POs */
  // if (!data?.EducationDetails?.length) {
  //   errors.RequireAtLeastOneItem.push('Education');
  // } else {
  //   errors.RequireAtLeastOneItem = errors.RequireAtLeastOneItem.filter((item) => item !== 'Education');
  // }

  return errors;
};

export const correctData = (obj, dayOffset = 0) => {
  const newObj = { ...obj };
  let startDate = new Date(newObj.StartDate);
  startDate.setDate(startDate.getDate() + dayOffset);
  startDate = dateFormat(startDate, 'isoUtcDateTime');
  let endDate = new Date(newObj.EndDate);
  endDate.setDate(endDate.getDate() + dayOffset);
  endDate = dateFormat(endDate, 'isoUtcDateTime');

  if (newObj.ValidUntil) {
    let validUntil = new Date(newObj.ValidUntil);
    validUntil.setDate(validUntil.getDate() + dayOffset);
    validUntil = dateFormat(validUntil, 'isoUtcDateTime');

    delete newObj.StartDate;
    delete newObj.EndDate;

    return { ...newObj, ValidUntil: validUntil };
  }

  return { ...newObj, StartDate: startDate, EndDate: endDate };
};

export const isValidDate = (date) => date instanceof Date && !Number.isNaN(date.getTime());

export const increaseDate = (date, numberOfDays) => {
  if (!isValidDate(new Date(date))) return undefined;

  let increasedDate = new Date(date);
  increasedDate.setDate(increasedDate.getDate() + numberOfDays);
  increasedDate = dateFormat(increasedDate, 'isoUtcDateTime');

  return increasedDate;
};

export const withDebounce = (func, delay, timerId, setTimer) => {
  if (timerId) {
    clearTimeout(timerId);
  }
  const timerDelayId = setTimeout(() => {
    setTimer(null);
    func();
  }, delay);
  setTimer(timerDelayId);
};

export const isJobActive = (item) => {
  if (item && item.Job) {
    if (!item.Job.IsActive) {
      return false;
    }
  }

  return true;
};

export const getLastDayOFMounth = (dateString) => {
  const date = new Date(dateString);
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
};

export const getFirstDayOFMonth = (dateString) => {
  const date = new Date(dateString);
  return new Date(date.getFullYear(), date.getMonth(), 1);
};

export const getFirstDayofWeek = (dateString) => {
  const date = new Date(dateString);
  const dayOfWeek = date.getDay();
  const firstDayOfWeek = new Date(date);

  const offset = dayOfWeek === 0 ? -6 : 1;
  firstDayOfWeek.setDate(date.getDate() - dayOfWeek + offset);

  return firstDayOfWeek;
};

export const parseAPIErrors = (errors) => {
  return Object.entries(errors).map((error) => {
    const block = error[0].substring(0, error[0].indexOf('['));
    const numberOfEntries = error[0].substring(error[0].indexOf('[') + 1, error[0].indexOf(']'));
    const property = error[0].substring(error[0].indexOf('.') + 1, error[0].length);
    const messages = error[1].join('/');
    return block
      ? `${block} -> Entry number: ${
          Number(numberOfEntries) + 1
        } -> Field: ${property}, has the following errors: ${messages}`
      : `Field: ${property}, has the following errors: ${messages}`;
  });
};

export const getCookie = (name) => {
  const matches = document.cookie.match(
    new RegExp(`(?:^|; )${name?.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1')}=([^;]*)`)
  );
  return matches ? decodeURIComponent(matches[1]) : undefined;
};

export const checkOnboardingCookie = () => {
  return getCookie(configData.TIFO_ONBOARDING_KEY)
    ? getCookie(configData.TIFO_ONBOARDING_KEY) === configData.TIFO_ONBOARDING_VALUE
    : false;
};

export const removeStyleAttribute = (htmlString) => {
  const regex = /style="[^"]*"/gi;
  return htmlString.replace(regex, '');
};

export const getNumberWithDelimeter = (num, isEdit) => {
  if (Number(num) === 0) return 0;
  return Number.isInteger(Number(num)) && isEdit ? `${num}.00` : num;
};

export const getHoursAndMinutes = (incomingHours) => {
  const hours = Number(parseFloat(incomingHours).toFixed(2).toString());
  const minutes = (hours - Math.trunc(hours)) * 60;
  return `${Math.trunc(hours)} h ${minutes !== 0 ? `${Math.floor(minutes)} m` : ''}`;
};

export const getValuesByKey = (initArray, originalKey, fromArray, byKey) => {
  const uniqArray = Array.from(new Set(initArray.map((item) => item?.[originalKey])));

  if (uniqArray.indexOf(undefined) === 0) {
    return initArray.map((item) => fromArray.find((value) => value?.[byKey] === item));
  }

  return uniqArray.map((item) => fromArray.find((value) => value?.[byKey] === item));
};

export const createLinkForSharingJob = (ref, id) => `${ref}/jobs/${id}`;

export const stringToHslColor = (str, s, l) => {
  let hash = 0;

  for (let i = 0; i < str.length; i++) {
    // eslint-disable-next-line no-bitwise -- intentional djb2-style string hash; << 5 relies on 32-bit int coercion
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }

  const h = hash % 360;
  return `hsl(${h}, ${s}%, ${l}%)`;
};

export const getAllDaysInMonth = (year, month) => {
  const date = dayjs(`${year}-${month}-01`);
  const daysInMonth = date.daysInMonth();

  return Array.from({ length: daysInMonth }, (_, index) => {
    return date.date(index + 1).format('YYYY-MM-DD');
  });
};

export const getBiMonthlyDays = (inputDate, isBiWeekly = false) => {
  const date = dayjs(inputDate);
  const year = date.year();
  const month = date.month() + 1;
  const day = date.date();
  const daysInMonth = date.daysInMonth();

  let startDay;
  let endDay;

  if (isBiWeekly) {
    startDay = day;
    endDay = day + 13;
  } else if (day <= 15) {
    startDay = 1;
    endDay = 15;
  } else {
    startDay = 16;
    endDay = daysInMonth;
  }

  return Array.from({ length: endDay - startDay + 1 }, (_, index) => {
    return dayjs(`${year}-${month}-${startDay + index}`).format('YYYY-MM-DD');
  });
};

export const buildMonth = (actualDate) => {
  const firstDayOfTheMonth = actualDate?.clone().startOf('month');

  const firstDayOfFirstWeekOfMonth = dayjs(firstDayOfTheMonth).startOf('week');

  const generateFirstDayOfEachWeek = (day) => {
    const dates = [day];
    for (let i = 1; i < 6; i++) {
      const dateFD = day.clone().add(i, 'week');
      dates.push(dateFD);
    }
    return dates;
  };

  const generateWeek = (day) => {
    const dates = [];
    for (let i = 1; i < 8; i++) {
      const dateW = day.clone().add(i, 'day').toDate();
      dates.push(dateW);
    }
    return dates;
  };

  const generateWeeksOfTheMonth = () => {
    const firstDayOfEachWeek = generateFirstDayOfEachWeek(firstDayOfFirstWeekOfMonth);
    return firstDayOfEachWeek.map((dateFDEW) => generateWeek(dateFDEW));
  };

  return generateWeeksOfTheMonth().filter((week) =>
    week.some((day) => day.getMonth() === new Date(firstDayOfTheMonth).getMonth())
  );
};

export const isValidByRateType = (value, type) => {
  const numberValue = Number(value);
  if (type === rateEnum.HOURLY && numberValue >= 0 && numberValue <= 24) return true;
  return type === rateEnum.DAILY && numberValue >= 0 && numberValue <= 1;
};

export const removeDuplicates = (arr) => {
  const tempObj = {};
  return arr.filter((obj) => {
    const key = JSON.stringify(obj);
    if (!tempObj[key]) {
      tempObj[key] = true;
      return true;
    }
    return false;
  });
};

export const basicErrorHandler = (dispatch, message, type = appNotificationsTypes.ERROR, duration = 3000) => {
  return dispatch(
    setNotification({
      type,
      message,
      duration
    })
  );
};

export const distanceDateFormat = (inputDate, isUTC = false) => {
  dayjs.extend(advancedFormat);
  const now = isUTC ? dayjs().add(-dayjs().utcOffset(), 'minutes') : dayjs();
  const secondsDiff = now.diff(inputDate, 'second');

  if (secondsDiff < 60) {
    return `A few seconds ago`;
  }
  if (secondsDiff > 60 && secondsDiff < 3600) {
    const minutes = Math.floor(secondsDiff / 60);
    return `${minutes} mins ago`;
  }
  if (secondsDiff > 3600 && secondsDiff < 86400) {
    const hours = Math.floor(secondsDiff / 3600);
    return `${hours} hour ago`;
  }
  if (secondsDiff > 86400 && secondsDiff < 172800) {
    return `Yesterday`;
  }

  return `${dayjs(inputDate).format(generalEnums.DATEWITHSTANDARD)}`;
};

export const convertToFormData = (json) => {
  const formData = new FormData();

  Object.entries(json).forEach((item) => {
    if (item[1] !== null) {
      formData.append(item[0], item[1]);
    }
  });

  return formData;
};

export const numberWithCommas = (num) => {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

export const clearMonth = (data, currentMonth) => {
  return data.map((item) => ({
    ...item,
    TimesheetHoursByDates: item?.TimesheetHoursByDates?.reduce((acc, elem) => {
      if (dayjs(elem.Day).month() === currentMonth) {
        return [...acc, elem];
      }
      return acc;
    }, [])
  }));
};

export const getSerializedSearchParams = () => {
  const searchParams = new URLSearchParams(window.location.search.slice(1));
  // Later duplicates overwrite earlier ones, matching the previous spread-in-loop behaviour.
  return Object.fromEntries(searchParams.entries());
};

export const getFullLocationString = (city, state, country, location = false) => {
  const resultString = location ? `${location}` : `${city || ''}${state ? `, ${state || ''}` : ''}, ${country || ''}`;
  return (
    resultString
      .trim()
      .replace(/^,+|,$/g, '')
      .trim() || 'Everywhere'
  );
};

export const setSearchParamConstructor = (filter, objName) => {
  let fields = '';
  Object.entries(filter).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.reduce((result, val) => {
        fields += val ? `${key}=${encodeURIComponent(val[objName] || val)}&` : '';
        return fields;
      }, []);
    } else {
      fields += value ? `${key}=${encodeURIComponent(value)}&` : '';
    }
  });

  return fields;
};

const decodeToken = (token) => {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split('')
      .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
      .join('')
  );
  return JSON.parse(jsonPayload);
};

export const isTokenExpired = (token) => {
  if (!token) return true;
  const decoded = decodeToken(token);
  const currentTime = Math.floor(Date.now() / 1000);
  return decoded.exp - currentTime < 300;
};

export const validatePassword = (password) => {
  const initValid = {
    charactersqty: false,
    lowercase: false,
    uppercase: false,
    numberorsymbol: false
  };

  const specialChars = /[0-9`~!@#$%^&*()[\]{}\-+=_|/<>"':.,_;?\\]/;

  if (password.length >= 8) {
    initValid.charactersqty = true;
  }

  if (
    password.split('').some((smb) => smb === smb.toLowerCase() && Number.isNaN(Number(smb)) && !specialChars.test(smb))
  ) {
    initValid.lowercase = true;
  }

  if (
    password.split('').some((smb) => smb === smb.toUpperCase() && Number.isNaN(Number(smb)) && !specialChars.test(smb))
  ) {
    initValid.uppercase = true;
  }

  if (password.match(/\d+/g) || specialChars.test(password)) {
    initValid.numberorsymbol = true;
  }

  return initValid;
};

export const useURLParams = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  return Object.fromEntries(params.entries());
};

export const useParentURL = () => {
  const [parentUrl, setParentUrl] = useState(null);
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data.type === 'PARENT_URL') {
        setParentUrl(event.data.url);
      }
    };

    window.addEventListener('message', handleMessage);
    window.parent.postMessage({ type: 'GET_PARENT_URL' }, '*');

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  return parentUrl;
};

export const calculateProfileCompletion = (assessmentArray) => {
  if (!Array.isArray(assessmentArray)) return 0;

  return assessmentArray.reduce((total, section) => {
    return total + (section.isSectionComplete ? section.percentage : 0);
  }, 0);
};

export function extractCountryCode(phoneNumber) {
  const cleaned = phoneNumber.replace(/[\s\-()]/g, '');
  const match = cleaned.match(/^(?:\+|00)(\d{1,4})/);

  return match ? match[1] : '';
}

export const getNormalizedLocation = (locationStr) => {
  if (!locationStr) return { City: '', State: '', Country: '' };

  const parts = locationStr.split(',').map((p) => p.trim());
  let city = '';
  let state = '';
  let countryRaw = '';

  if (parts.length >= 3) {
    [city, state, countryRaw] = parts.slice(-3);
  } else if (parts.length === 2) {
    [city, countryRaw] = parts;
  } else {
    [countryRaw] = parts;
  }

  const normalizedCountry =
    countries.getName(countryRaw, 'en') ||
    countries.getName(countries.getAlpha2Code(countryRaw, 'en'), 'en') ||
    countryRaw;
  return { City: city, State: state, Country: normalizedCountry };
};

export const formatHours = (n) => (n % 1 === 0 ? n : parseFloat(n.toFixed(2)));
